import { fromBech32, toHex } from "@cosmjs/encoding";
import { sha256 } from "rujira.ui";
import {
  REDACTED_PROVING_KEY_SHA256,
  REDACTED_PROVING_KEY_URL,
  REDACTED_PROVING_WASM_SHA256,
  REDACTED_PROVING_WASM_URL,
} from "./config";

type ProofCallData = [
  [string, string],
  [[string, string], [string, string]],
  [string, string],
  readonly string[],
];

let verifiedArtifactUrls: Promise<{ wasm: string; provingKey: string }> | null =
  null;

const bytesToHex = (bytes: ArrayBuffer): string =>
  [...new Uint8Array(bytes)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

const verifiedBlobUrl = async (
  url: string,
  expectedSha256: string
): Promise<string> => {
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) {
    throw new Error(
      `Unable to load Redacted proving artifact (${response.status})`
    );
  }
  const bytes = await response.arrayBuffer();
  const digest = bytesToHex(await crypto.subtle.digest("SHA-256", bytes));
  if (digest !== expectedSha256) {
    throw new Error("Redacted proving artifact checksum mismatch");
  }
  return URL.createObjectURL(new Blob([bytes]));
};

const provingArtifacts = () => {
  if (!verifiedArtifactUrls) {
    verifiedArtifactUrls = Promise.all([
      verifiedBlobUrl(REDACTED_PROVING_WASM_URL, REDACTED_PROVING_WASM_SHA256),
      verifiedBlobUrl(REDACTED_PROVING_KEY_URL, REDACTED_PROVING_KEY_SHA256),
    ]).then(([wasm, provingKey]) => ({ wasm, provingKey }));
  }
  return verifiedArtifactUrls;
};

export const deriveStealth = async (
  address: string,
  derivationSignature: string
): Promise<string> => {
  const [{ buildPoseidon }, secret] = await Promise.all([
    import("circomlibjs"),
    sha256(derivationSignature),
  ]);
  const poseidon = await buildPoseidon();
  const encodedAddress = `0x${toHex(fromBech32(address).data)}`;
  const hash = poseidon.F.toString(poseidon([encodedAddress, secret]));
  return `0x${BigInt(hash).toString(16)}`;
};

export const generateLocalProof = async (params: {
  address: string;
  derivationSignature: string;
  stealth: string;
  param: string;
}): Promise<string[]> => {
  const [{ groth16 }, artifacts, secret] = await Promise.all([
    import("snarkjs"),
    provingArtifacts(),
    sha256(params.derivationSignature),
  ]);

  const encodedAddress = `0x${toHex(fromBech32(params.address).data)}`;
  const stealth = params.stealth.startsWith("0x")
    ? params.stealth
    : `0x${params.stealth}`;
  const { proof, publicSignals } = await groth16.fullProve(
    {
      address: encodedAddress,
      secret,
      stealth_hash: stealth,
      param: params.param,
    },
    artifacts.wasm,
    artifacts.provingKey
  );
  const callData = await groth16.exportSolidityCallData(proof, publicSignals);
  const parsed = JSON.parse(`[${callData}]`) as ProofCallData;

  return [
    parsed[0][0],
    parsed[0][1],
    parsed[1][0][0],
    parsed[1][0][1],
    parsed[1][1][0],
    parsed[1][1][1],
    parsed[2][0],
    parsed[2][1],
  ];
};
